import { z } from 'zod';
import type { ZodTypeDef } from 'zod';

import type {
  CatalogProviderList as ApiCatalogProviderList,
  CatalogProviderListingSummary as ApiCatalogProviderListingSummary,
  CatalogProviderSummary as ApiCatalogProviderSummary,
  CatalogProviderWaitlistSummary as ApiCatalogProviderWaitlistSummary
} from '../../../../../packages/api/src/domain/catalog/providers/service';

const coerceNumberLike = (value: unknown) => {
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : value;
  }
  return value;
};
const nullableString = z.preprocess(
  (val) => (val === undefined ? null : val),
  z.string().nullable()
);
const nullableNumber = z.preprocess((value: unknown) => {
  if (value === null || value === undefined) {
    return null;
  }
  return coerceNumberLike(value);
}, z.number().nullable());
const nullableBoolean = z.boolean().nullable();
const integerNumberLike = z.preprocess(coerceNumberLike, z.number().int());
const nonNegativeInt = z.preprocess(
  coerceNumberLike,
  z.number().int().nonnegative()
);
const positiveInt = z.preprocess(coerceNumberLike, z.number().int().min(1));

export type CatalogProviderListingSummary = ApiCatalogProviderListingSummary;

export type CatalogProviderWaitlistSummary = ApiCatalogProviderWaitlistSummary;

export type CatalogProviderSummary = ApiCatalogProviderSummary;

export type CatalogProviderList = ApiCatalogProviderList;

export const catalogProviderListingSummarySchema = z.object({
  id: integerNumberLike,
  slug: nullableString,
  shortId: nullableString,
  title: nullableString,
  breed: nullableString,
  breedSlug: nullableString,
  price: nullableNumber,
  depositAmount: nullableNumber,
  isAvailableNow: nullableBoolean,
  availableDate: nullableString,
  isRehomed: nullableBoolean,
  isReserved: z.boolean(),
  updatedAt: nullableString,
  imageUrl: nullableString,
  sex: nullableString
}) satisfies z.ZodType<CatalogProviderListingSummary, ZodTypeDef, unknown>;
export const catalogProviderWaitlistSummarySchema = z.object({
  id: integerNumberLike,
  slug: nullableString,
  shortId: nullableString,
  title: nullableString,
  breed: nullableString,
  breedSlug: nullableString,
  priceMin: nullableNumber,
  priceMax: nullableNumber,
  estimatedDateOfBirth: nullableString,
  updatedAt: nullableString,
  imageUrl: nullableString
}) satisfies z.ZodType<CatalogProviderWaitlistSummary, ZodTypeDef, unknown>;
export const catalogProviderSummarySchema = z.object({
  id: integerNumberLike,
  legacyId: z.string(),
  slug: z.string(),
  name: z.string(),
  city: nullableString,
  province: nullableString,
  provinceSlug: nullableString,
  imageUrl: nullableString,
  averageRating: nullableNumber,
  numberOfRatings: nullableNumber,
  isIdVerified: z.boolean(),
  listings: z.array(catalogProviderListingSummarySchema),
  waitlists: z.array(catalogProviderWaitlistSummarySchema),
  geo: z.record(z.unknown()).nullable(),
  latitude: nullableNumber,
  longitude: nullableNumber,
  distanceMeters: nullableNumber,
  createdAt: nullableString,
  updatedAt: nullableString
}) satisfies z.ZodType<CatalogProviderSummary, ZodTypeDef, unknown>;
export const catalogProviderListSchema = z.object({
  items: z.array(catalogProviderSummarySchema),
  total: nonNegativeInt,
  totalItems: nonNegativeInt.default(0),
  page: positiveInt,
  limit: positiveInt,
  totalPages: nonNegativeInt
}) satisfies z.ZodType<CatalogProviderList, ZodTypeDef, unknown>;
